import React, { useEffect, Fragment, useState, useRef } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { connect, useDispatch } from "react-redux";
import * as XLSX from "xlsx";
import PropTypes, { string } from "prop-types";
import {
  loadUsersList,
  clearUser,
  importUsers,
  loadSchoolsList,
  deleteMultipleUsers,
  validateUser,
} from "../../../actions/admin";

import UsersList from "./UsersList";
import { usePersistedTableState } from "../../../hooks/usePersistedTableState";
import { setAlert } from "../../../actions/alerts";
import DeleteConfirmationModal from "../../layouts/DeleteConfirmationModal";
import Loading from "../../layouts/Loading";

const Users = ({
  usersList,
  loadUsersList,
  usersListLoading,
  clearUser,
  user,
  importUsers,
  loadSchoolsList,
  deleteMultipleUsers,
  validateUser,
  schoolsList,
}) => {
  const [isLoadingImportUsers, setIsLoadingImportUsers] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [showAppliedEffect, setShowAppliedEffect] = useState(false); // UI only
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isFirstUsersLoad, setIsFirstUsersLoad] = useState(
    () => !sessionStorage.getItem("usersPageLoadedOnce"),
  );
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const defaultFilterState = {
    SchoolIDs: [],
    UserTypeIDs: [],
    GradeIDs: [],
  };
  const {
    page,
    setPage,
    searchQuery,
    setSearchQuery,
    persistentFilters,
    updateFilter,
    setFilters,
  } = usePersistedTableState("users", defaultFilterState);
  const [filterData, setFilterData] = useState(persistentFilters);
  const [file, setFile] = useState(null);
  const [warningMessage, setWarningMessage] = useState("");
  const [isLoadingValidateForm, setIsLoadingValidateForm] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const hist = useHistory();
  const dispatch = useDispatch();

  const isUsersLoaded = useRef(false);
  const isFilterEffectFirstRun = useRef(true);
  const hasActiveFilters = (filters) =>
    Object.values(filters).some(
      (value) => Array.isArray(value) && value.length > 0,
    );

  useEffect(() => {
    const currentFilters = JSON.stringify(filterData);
    const persistedFiltersString = JSON.stringify(persistentFilters);

    if (currentFilters !== persistedFiltersString) {
      setFilterData(persistentFilters);
    }
  }, [persistentFilters]);

  useEffect(() => {
    const currentFilters = JSON.stringify(filterData);
    const persistedFiltersString = JSON.stringify(persistentFilters);

    if (currentFilters !== persistedFiltersString) {
      setFilters(filterData);
    }
  }, [filterData, persistentFilters, setFilters]);
  const getFirstSchoolID = () => {
    const schoolIds = user?.schoolIds
      ? String(user.schoolIds)
          .split(",")
          .map((id) => id.trim())
      : [];

    if (schoolIds.length > 0 && schoolIds[0]) {
      return [Number(schoolIds[0])];
    }

    return user?.SchoolID ? [user.SchoolID] : [];
  };

  // useEffect(() => {
  //   // Admin role: Retrive all users from the Admin's school list
  //   if (user.UserTypeID === 6) {
  //     loadUsersList({ schoolIds: user.schoolIds });
  //   }
  //   // Super Admin role and other roles: Retrive all users from all schools
  //   else {
  //     loadUsersList();
  //   }
  // }, [user, clearUser]);

  useEffect(() => {
    const firstSchoolID = getFirstSchoolID();
    if (
      firstSchoolID.length === 0 &&
      (!schoolsList || schoolsList.length === 0)
    ) {
      return;
    }
    // Prevent duplicate API calls on same page load
    if (isUsersLoaded.current) {
      return;
    }
    const defaultSchools =
      firstSchoolID.length > 0
        ? firstSchoolID
        : schoolsList?.length > 0
          ? [schoolsList[0].SchoolID]
          : [];

    isUsersLoaded.current = true;
    setFilterData((prev) => ({
      ...prev,
      SchoolIDs: prev.SchoolIDs.length > 0 ? prev.SchoolIDs : defaultSchools,
    }));

    // Dispatch initial fetch with the first school default
    // loadUsersList({
    //   ...filterData,
    //   SchoolIDs: defaultSchools,
    // });
  }, [user?.schoolIds, user?.SchoolID, schoolsList]); // Syncs when auth state loads

  useEffect(() => {
    if (!hasActiveFilters(filterData)) return;

    const shouldShowFilterLoading = !isFilterEffectFirstRun.current;
    if (isFilterEffectFirstRun.current) {
      isFilterEffectFirstRun.current = false;
    }

    if (shouldShowFilterLoading) {
      setIsFilterLoading(true);
    }

    let isActive = true;
    const timeoutId = setTimeout(() => {
      loadUsersList({
        ...filterData,
      }).finally(() => {
        if (isActive && shouldShowFilterLoading) {
          setIsFilterLoading(false);
        }
      });
    }, 1000);

    return () => {
      isActive = false;
      clearTimeout(timeoutId);
    };
  }, [filterData]);

  useEffect(() => {
    if (!usersListLoading && isFirstUsersLoad) {
      setIsFirstUsersLoad(false);
      sessionStorage.setItem("usersPageLoadedOnce", "true");
    }
  }, [usersListLoading, isFirstUsersLoad]);

  const formatStudents = (list) => {
    let arr = [];
    for (let i = 0; i < list.length; i++) {
      arr.push(list[i].StudentID);
    }
    return arr;
  };

  const handleApplyFilter = () => {
    const payload = {
      ...filterData,
      // Fallback to user default schools if no explicit school selected in filter UI
      SchoolIDs:
        filterData.SchoolIDs.length > 0
          ? filterData.SchoolIDs
          : getFirstSchoolID(),
    };
    setFiltersApplied(true);
    setIsFilterLoading(true);
    loadUsersList(payload).finally(() => setIsFilterLoading(false));
    // Temporary success effect
    setShowAppliedEffect(true);

    setTimeout(() => {
      setShowAppliedEffect(false);
    }, 1000);
  };
  const handleClearFilter = () => {
    const firstSchoolID = getFirstSchoolID();
    const defaultSchools =
      firstSchoolID.length > 0
        ? firstSchoolID
        : schoolsList?.length > 0
          ? [schoolsList[0].SchoolID]
          : [];
    setFilterData({
      SchoolIDs: defaultSchools,
      UserTypeIDs: [],
      GradeIDs: [],
    });
    setFiltersApplied(false);
    setIsFilterLoading(true);
  };

  const showUsersLoader =
    (usersListLoading && isFirstUsersLoad) || isFilterLoading;

  if (isLoadingValidateForm)
    return (
      <div
        className="p-sm-5 p-2 w-100 dashboard-margin"
        style={{ marginTop: "200px" }}
      >
        <div className="mb-3 ">
          <div className="d-flex w-100 align-items-center justify-content-center">
            <Loading />
          </div>
        </div>
      </div>
    );

  return (
    <Fragment>
      <div className="p-sm-5 p-2 w-100 dashboard-margin">
        <div className="mb-3 ">
          <div className="d-flex align-items-center">
            <h6 className="txt-primary-light mb-0">{user.UserType} / Users</h6>
            <div className="rounded-pill bg-primary px-2 py-1 align-self-center mx-2 my-2 caption ">
              {user.UserTypeID === 1 &&
              user.Students !== undefined &&
              user.Students.length
                ? usersList.filter((item) => item.UserTypeID === 2).length
                : user.UserTypeID === 1 && user.Students.length === 0
                  ? 0
                  : usersList.length}
            </div>
          </div>
          <div className="d-flex w-100 align-items-center justify-content-end">
            {selectedUsers.length > 0 && (
              <button
                className="btn btn-danger d-flex align-items-center px-4 me-3"
                data-bs-toggle="modal"
                data-bs-target="#deleteMultipleUsers"
                // onClick={() => handleBulkDelete(selectedSchools)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  className="bi bi-trash-fill button-child me-2"
                  viewBox="0 0 16 16"
                >
                  <path d="M2.5 1a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1H3v9a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V4h.5a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H10a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1H2.5z" />
                </svg>
                Delete ({selectedUsers.length})
              </button>
            )}
            {parseInt(user.UserTypeID) !== 1 && (
              <div className="d-flex">
                <a
                  href={process.env.PUBLIC_URL + "/templates/users.xlsx"}
                  download
                  className="btn button-parent button-primary d-flex align-items-center px-3 mx-3"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    class="bi bi-download button-child"
                    viewBox="0 0 16 16"
                  >
                    <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                    <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
                  </svg>
                  Template
                </a>

                <div
                  className="btn button-parent button-primary d-flex align-items-center px-3 mx-3"
                  data-bs-toggle="modal"
                  data-bs-target="#ImportUser"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    enableBackground="new 0 0 24 24"
                    height="18px"
                    viewBox="0 0 24 24"
                    width="24px"
                    fill="#ffffff"
                    className="button-child"
                  >
                    <g>
                      <rect fill="none" height="18" width="18" />
                    </g>
                    <g>
                      <g>
                        <path d="M19,13h-6v6h-2v-6H5v-2h6V5h2v6h6V13z" />
                      </g>
                    </g>
                  </svg>
                  Import Excel
                </div>
                <div
                  className="btn button-parent button-primary d-flex align-items-center px-3"
                  onClick={() => {
                    clearUser();
                    loadSchoolsList({ schoolIds: user.schoolIds }).then(() =>
                      hist.push("/admin/user/add"),
                    );
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    enableBackground="new 0 0 24 24"
                    height="18px"
                    viewBox="0 0 24 24"
                    width="24px"
                    fill="#ffffff"
                    className="button-child"
                  >
                    <g>
                      <rect fill="none" height="18" width="18" />
                    </g>
                    <g>
                      <g>
                        <path d="M19,13h-6v6h-2v-6H5v-2h6V5h2v6h6V13z" />
                      </g>
                    </g>
                  </svg>
                  Add User
                </div>
              </div>
            )}
          </div>
        </div>

        <UsersList
          usersList={
            user.Students === undefined
              ? usersList
              : user.UserTypeID === 6
                ? usersList.filter((user) =>
                    user.schoolIds
                      .split(",")
                      .map((id) => parseInt(id.trim()))
                      .includes(user.SchoolID),
                  )
                : usersList.filter((y) =>
                    formatStudents(user.Students).includes(y.UserID),
                  )
          }
          usersListLoading={usersListLoading || isLoadingImportUsers}
          showLoader={showUsersLoader}
          userAuth={user}
          deleteMultipleUsers={deleteMultipleUsers}
          filterData={filterData}
          setFilterData={setFilterData}
          handleApplyFilter={handleApplyFilter}
          handleClearFilter={handleClearFilter}
          filtersApplied={filtersApplied}
          showAppliedEffect={showAppliedEffect}
          selectedUsers={selectedUsers}
          setSelectedUsers={setSelectedUsers}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          page={page}
          setPage={setPage}
        />
      </div>
      <DeleteConfirmationModal
        id="deleteMultipleUsers"
        title="Delete User(s)"
        message="Are you sure you want to delete user(s)?"
        warning="Deleting a user will result in deleting everything related to it."
        onConfirm={() => {
          // deleteSchool(SchoolID);
          // hist.push("/admin/schools");
          deleteMultipleUsers(selectedUsers, filterData);
          setSelectedUsers([]);
          // clearSchool();
        }}
      />
      <div
        className="modal fade"
        id="ImportUser"
        aria-labelledby="ImportUserLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="ImportUserLabel">
                Import Users
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <input
                type="file"
                accept=".xlsx"
                onChange={(e) => {
                  e.preventDefault();
                  var files = e.target.files,
                    f = files[0];
                  var reader = new FileReader();
                  reader.onload = function (e) {
                    var data = e.target.result;
                    let readedData = XLSX.read(data, { type: "binary" });
                    const wsname = readedData.SheetNames[0];
                    const ws = readedData.Sheets[wsname];

                    /* Convert array to json*/
                    const dataParse = XLSX.utils.sheet_to_json(ws, {
                      header: 1,
                    });
                    dataParse.splice(0, 1);
                    let d = [];
                    dataParse.forEach((user) => {
                      if (user.length !== 0) {
                        d.push({
                          col_0: user[6],
                          col_1: user[0],
                          col_2: user[1],
                          col_3: user[3],

                          FirstName: user[0],
                          LastName: user[1],
                          SchoolID: user[2] === undefined ? null : user[2],
                          UserTypeID: user[3],
                          Email:
                            parseInt(user[3]) === 2
                              ? `${
                                  user[0] +
                                  user[1] +
                                  user[2] +
                                  Math.floor(Math.random() * 1000)
                                }@mail.com`
                              : user[4],
                          Password:
                            parseInt(user[3]) === 2
                              ? "121233212321312"
                              : user[5],
                          AlternativeID: user[6] === undefined ? null : user[6],
                        });
                      }
                    });

                    setFile(d);
                  };
                  reader.readAsBinaryString(f);
                }}
              />
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn button-primary"
                onClick={async () => {
                  const studentsForValidation = file.map((student) => ({
                    col_0: String(student.col_0),
                    col_1: student.col_1,
                    col_2: student.col_2,
                    col_3: student.col_3,
                  }));

                  setIsLoadingValidateForm(true);
                  await validateUser(studentsForValidation)
                    .then((res) => {
                      if (!res.success) {
                        const newStudents = `${res.errors.Summary["NewStudents"]} New User(s) 🔵`;
                        const closeMatches = `${res.errors.Summary["CloseMatches"]} Close Match(es) 🔴`;
                        const exactMatches = `${res.errors.Summary["ExactMatches"]} Exact Match(es) 🟢`;
                        const exactMatchMessages =
                          res.errors.Messages.exactMatches
                            .map((msg) =>
                              msg.replace(" and added to your classroom.", ""),
                            )
                            .join("\n");

                        const closeMatchMessages =
                          res.errors.Messages.closeMatches
                            .map((msg) =>
                              msg.replace(" and added to your classroom.", ""),
                            )
                            .join("\n");

                        const unMatchMessages = res.errors.Messages.unMatches
                          .map((msg) =>
                            msg.replace(" and added to your classroom.", ""),
                          )
                          .join("\n");

                        const consolidateMessages = `Summary of Users: \n${newStudents}\n${closeMatches}\n${exactMatches}\n\n${unMatchMessages}\n${closeMatchMessages}\n${exactMatchMessages}\n`;
                        setIsLoadingValidateForm(false);
                        setWarningMessage(consolidateMessages);
                        return false;
                      }
                    })
                    .catch(() => {
                      setWarningMessage("Something went wrong");
                    })
                    .finally(() => {
                      setIsLoadingValidateForm(false);
                      setShowWarning(true);
                    });
                }}
                data-bs-dismiss="modal"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Warning Modal */}
      {showWarning && warningMessage && (
        <Modal
          show={showWarning}
          onHide={() => setShowWarning(false)}
          centered
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>Warning</Modal.Title>
          </Modal.Header>
          <Modal.Body className="d-flex flex-column gap-2">
            {warningMessage.split("\n").map((line, idx) => (
              <div key={idx}>{line}</div>
            ))}

            <div className="alert alert-info mt-4">
              <div className="d-flex align-items-start">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  fill="#0c5460"
                  className="bi bi-info-circle-fill me-2 mt-1 flex-shrink-0"
                  viewBox="0 0 16 16"
                >
                  <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z" />
                </svg>
                <div>
                  <strong>Note:</strong>
                  <br />

                  <>
                    - Once created, the user can be edited later if needed.
                    <br />
                    - Exactly matched users will not be re-created.
                    <br />- Closely matched and unmatched users will be created.
                  </>
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowWarning(false);
              }}
            >
              Close
            </Button>
            {!warningMessage.includes("Something went wrong") && (
              <Button
                variant="success"
                // onClick={() => {
                //   setShowWarning(false);
                // }}
                onClick={() => {
                  setShowWarning(false);
                  setIsLoadingImportUsers(true);
                  importUsers({ users: file }).then(async (res) => {
                    if (res?.success) {
                      await loadUsersList(filterData);
                    } else {
                      dispatch(
                        setAlert(
                          "Something went wrong while importing users, Try again and verify if all the needed fields are filled correctly.",
                          "danger",
                          5000,
                        ),
                      );
                    }
                    setIsLoadingImportUsers(false);
                  });
                }}
              >
                Confirm
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      )}
    </Fragment>
  );
};
Users.propTypes = {
  usersList: PropTypes.array,
  loadUsersList: PropTypes.func.isRequired,
  deleteMultipleUsers: PropTypes.func.isRequired,
  usersListLoading: PropTypes.bool,
  clearUser: PropTypes.func.isRequired,
  user: PropTypes.object,
  loadSchoolsList: PropTypes.func.isRequired,
  schoolsList: PropTypes.array,
};
const mapStateToProps = (state) => ({
  usersList: state.admin.usersList,
  usersListLoading: state.admin.usersListLoading,
  user: state.auth.user,
  schoolsList: state.admin.schoolsList,
});

export default connect(mapStateToProps, {
  loadUsersList,
  clearUser,
  importUsers,
  loadSchoolsList,
  deleteMultipleUsers,
  validateUser,
})(Users);
